import { trace } from '@opentelemetry/api';

import { ErrorHandler } from './errorHandling';
import type { Tool } from './tools';
import type {
    CodeInterpreterOutput,
    ConversationMessage,
    ImageContent,
    TextContent,
    ToolCall,
    ToolDefinition,
    ToolExecutionContext,
    Usage,
} from './types';
import { isAssistantMessage, MessageRole } from './types';

import type { Conversation } from 'js/src/agent/Conversation';
import { yieldToBrowser } from 'js/src/agent/yieldToBrowser';
import { setTag as setSentryTag } from 'js/src/libs/sentry';

const toolExecutorTracer = trace.getTracer('lumen5-agent-tools');

export interface ToolExecutionResult {
    success: boolean;
    result: string;
    iterationsUsed: number;
}

// Shown verbatim by any root agent that hits the cap — keep agent-agnostic.
export const MAX_ITERATIONS_NOTICE =
    "I reached my tool-call limit for this step before finishing, so I've stopped here. " +
    "Tell me to continue and I'll pick up from where I left off.";

/**
 * Resolve the tool_calls the loop queued but never ran. Without this they stick
 * the spinner and invalidate the next turn (the API requires a tool_result for
 * every tool_call), so the user can't continue.
 */
export function finalizeUnrunToolCalls(
    conversation: Pick<Conversation, 'addToolMessage' | 'addAssistantMessage'>,
    pendingToolCalls: ToolCall[] | null,
): void {
    for (const toolCall of pendingToolCalls ?? []) {
        conversation.addToolMessage(
            'Skipped — reached the tool-call limit for this step before this tool ran.',
            toolCall.id,
            false,
        );
    }
    conversation.addAssistantMessage(MAX_ITERATIONS_NOTICE);
}

/**
 * Handles the execution of tools and manages the iterative process where the LLM calls tools,
 * receives results, and may call additional tools based on those results.
 *
 * This class manages the complex flow of:
 * 1. Executing tool calls from the LLM
 * 2. Adding tool results to the conversation
 * 3. Getting new LLM responses that may include more tool calls
 * 4. Repeating until no more tools are called or max iterations reached
 */
export class ToolExecutor {
    public tools: Tool[];
    private maxIterations: number;
    private pendingUserMessages: ConversationMessage[] = [];
    private shouldStopConversation = false;

    constructor(tools: Tool[], maxIterations: number = 15) {
        this.tools = tools;
        this.maxIterations = maxIterations;
    }

    /**
     * Executes a batch of tool calls and adds their results to the conversation.
     * Async tools are executed in parallel first, then synchronous tools are executed in series.
     * Each tool call is validated, executed, and any errors are caught and formatted.
     *
     * @param toolCalls Array of tool calls to execute
     * @param conversation Conversation to add tool results to
     * @param context Execution context including API key and model info
     */
    async executeTools(
        toolCalls: ToolCall[],
        conversation: Conversation,
        context: Omit<ToolExecutionContext, 'toolCallId'>,
    ): Promise<void> {
        // Clear state from previous tool executions
        this.pendingUserMessages = [];
        this.shouldStopConversation = false;

        // Separate async and sync tool calls
        const asyncToolCalls: ToolCall[] = [];
        const syncToolCalls: ToolCall[] = [];

        for (const toolCall of toolCalls) {
            const tool = this.tools.find(
                (t) => t.getToolDefinition().function.name === toolCall.function.name,
            );

            if (!tool) {
                conversation.addToolMessage(
                    `Error: Unknown tool ${toolCall.function.name}`,
                    toolCall.id,
                    false,
                );
                continue;
            }

            if (tool.canRunAsync) {
                asyncToolCalls.push(toolCall);
            } else {
                syncToolCalls.push(toolCall);
            }
        }

        // Execute async tools in parallel first
        if (asyncToolCalls.length > 0) {
            const asyncPromises = asyncToolCalls.map((toolCall) =>
                this.executeToolCall(toolCall, conversation, context),
            );
            await Promise.all(asyncPromises);
        }

        // In series, handing control back between tools: a batch of sync tools
        // otherwise runs as one uninterrupted microtask loop, so nothing repaints and
        // queued input doesn't register until the whole batch finishes.
        for (let i = 0; i < syncToolCalls.length; i++) {
            await this.executeToolCall(syncToolCalls[i], conversation, context);
            if (i < syncToolCalls.length - 1) {
                await yieldToBrowser();
            }
        }
    }

    /**
     * Executes a single tool call and adds the result to the conversation
     */
    private async executeToolCall(
        toolCall: ToolCall,
        conversation: Conversation,
        context: Omit<ToolExecutionContext, 'toolCallId'>,
    ): Promise<void> {
        // Capture start time IMMEDIATELY before any async operations
        // This ensures accurate timing even if tools execute sequentially
        const startTime = performance.now();

        // Find the tool that matches this tool call
        const tool = this.tools.find(
            (t) => t.getToolDefinition().function.name === toolCall.function.name,
        );

        if (!tool) {
            conversation.addToolMessage(
                `Error: Unknown tool ${toolCall.function.name}`,
                toolCall.id,
                false,
            );
            return;
        }

        // Tag Sentry replay with tool name for easy filtering (format: tool-{toolName})
        const toolTagName = `tool-${toolCall.function.name}`;
        setSentryTag(toolTagName, 'true');

        // Track tool call in analytics
        analytics.track('Agent Tool Called', {
            tool_name: toolCall.function.name,
            tool_call_id: toolCall.id,
            root_agent: context.task || 'unknown',
            can_run_async: tool.canRunAsync,
        });

        await toolExecutorTracer.startActiveSpan('tool-execution', async (span) => {
            span.setAttributes({
                'tool.name': toolCall.function.name,
                'tool.call_id': toolCall.id,
                'tool.arguments_length': toolCall.function.arguments.length,
                'tool.can_run_async': tool.canRunAsync,
                'agent.task': context.task || 'unknown',
            });

            try {
                // Parse arguments and execute the tool with enhanced validation
                const args = JSON.parse(toolCall.function.arguments);
                const toolContext = {
                    ...context,
                    toolCallId: toolCall.id,
                    conversationId: conversation.getId(),
                };
                const enhancedResult = await tool.executeWithValidation(args, toolContext);

                const latency = performance.now() - startTime;

                // Add tool result to conversation with success information and latency
                conversation.addToolMessage(
                    enhancedResult.result,
                    toolCall.id,
                    enhancedResult.isSuccess,
                    {
                        input_tokens: 0,
                        output_tokens: 0,
                        total_tokens: 0,
                        latency,
                    },
                    enhancedResult.resultContent,
                );

                // Track tool failure in analytics
                if (!enhancedResult.isSuccess) {
                    analytics.track('Agent Tool Failed', {
                        tool_name: toolCall.function.name,
                        tool_call_id: toolCall.id,
                        root_agent: context.task || 'unknown',
                        latency_ms: latency,
                        tool_result: enhancedResult.result,
                        conversation_url: `${window.location.origin}/conversations/${conversation.getId()}/`,
                    });
                }

                span.setStatus({ code: 1 });
                span.setAttributes({
                    'tool.result_length': enhancedResult.result.length,
                    'tool.inject_user_message': !!enhancedResult.injectAsUserMessage,
                    'tool.latency_ms': latency,
                });

                if (enhancedResult.stopConversation && enhancedResult.isSuccess) {
                    this.shouldStopConversation = true;
                }

                // A reference video the tool fetched: link it so the transcript carries the video
                // being imitated, not just its annotation. Appended after this tool's result, so it
                // reads in the order it happened.
                if (enhancedResult.videoDataSnapshot) {
                    conversation.uploadVideoDataSnapshot(
                        enhancedResult.videoDataSnapshot as Parameters<
                            Conversation['uploadVideoDataSnapshot']
                        >[0],
                        { label: enhancedResult.videoDataSnapshotLabel },
                    );
                }

                // If tool requested user message injection, store it for later
                if (enhancedResult.injectAsUserMessage) {
                    this.pendingUserMessages.push({
                        role: MessageRole.USER,
                        content: enhancedResult.injectAsUserMessage,
                    });
                }
            } catch (error) {
                const latency = performance.now() - startTime;

                // Handle tool execution errors consistently
                const errorMessage = ErrorHandler.formatToolError(
                    toolCall.function.name,
                    error instanceof Error ? error : new Error('Unknown error occurred'),
                );
                conversation.addToolMessage(errorMessage, toolCall.id, false, {
                    input_tokens: 0,
                    output_tokens: 0,
                    total_tokens: 0,
                    latency,
                });

                // Track tool failure in analytics
                analytics.track('Agent Tool Failed', {
                    tool_name: toolCall.function.name,
                    tool_call_id: toolCall.id,
                    root_agent: context.task || 'unknown',
                    latency_ms: latency,
                    error_message: errorMessage,
                    conversation_url: `${window.location.origin}/conversations/${conversation.getId()}/`,
                });

                span.recordException(
                    error instanceof Error ? error : new Error('Unknown error occurred'),
                );
                span.setStatus({ code: 2, message: errorMessage });
            } finally {
                span.end();
            }
        });
    }

    /**
     * Manages the iterative tool execution process where the LLM may call tools multiple times.
     * This is the main orchestration method that handles the complex flow of:
     * 1. Execute tools -> 2. Get LLM response -> 3. Check for more tool calls -> 4. Repeat
     *
     * @param initialToolCalls First batch of tool calls to execute
     * @param conversation Conversation to manage
     * @param context Execution context
     * @param onIteration Callback to get next LLM response after tool execution
     * @returns Result indicating success/failure and iterations used
     */
    async executeWithIterations(
        initialToolCalls: ToolCall[],
        conversation: Conversation,
        context: Omit<ToolExecutionContext, 'toolCallId'>,
        onIteration: (
            messages: ConversationMessage[],
            toolDefinitions: ToolDefinition[],
        ) => Promise<{
            content: string;
            tool_calls?: ToolCall[];
            usage: Usage;
            code_interpreter_outputs?: CodeInterpreterOutput[];
            openai_response_id?: string;
        }>,
    ): Promise<ToolExecutionResult> {
        return toolExecutorTracer.startActiveSpan('tool-iteration-cycle', async (span) => {
            span.setAttributes({
                'tools.initial_count': initialToolCalls.length,
                'tools.max_iterations': this.maxIterations,
                'agent.task': context.task || 'unknown',
            });

            let currentIteration = 0;
            let currentToolCalls = initialToolCalls;

            // Track cumulative token usage across all iterations
            let cumulativePromptTokens = 0;
            let cumulativeCompletionTokens = 0;
            let cumulativeTotalTokens = 0;

            try {
                while (currentIteration < this.maxIterations) {
                    if (context.abortSignal?.aborted) {
                        span.setStatus({ code: 2, message: 'Tool execution was cancelled' });
                        return {
                            success: false,
                            result: 'Tool execution was cancelled',
                            iterationsUsed: currentIteration,
                        };
                    }

                    if (currentToolCalls && currentToolCalls.length > 0) {
                        // Execute the current batch of tool calls
                        await this.executeTools(currentToolCalls, conversation, context);
                        currentIteration++;

                        span.setAttributes({
                            'tools.current_iteration': currentIteration,
                        });

                        // Check if there are any pending user messages to inject before LLM responds
                        if (this.pendingUserMessages.length > 0) {
                            // Add all pending user messages to the conversation
                            for (const userMessage of this.pendingUserMessages) {
                                await conversation.addUserMessage(userMessage.content);
                            }
                            // Clear pending messages after injection
                            this.pendingUserMessages = [];
                        }

                        // check if we called the stop tool
                        if (context.stopTool) {
                            const allToolCallNames = currentToolCalls.map((tc) => tc.function.name);
                            if (allToolCallNames.includes(context.stopTool)) {
                                span.setStatus({ code: 1 });
                                span.setAttributes({
                                    'tools.iterations_used': currentIteration,
                                    'tools.stopped_early': true,
                                    'tools.stop_tool': context.stopTool,
                                    'tools.cumulative_prompt_tokens': cumulativePromptTokens,
                                    'tools.cumulative_completion_tokens':
                                        cumulativeCompletionTokens,
                                    'tools.cumulative_total_tokens': cumulativeTotalTokens,
                                });
                                return {
                                    success: true,
                                    result: `Stopped early due to stop tool "${context.stopTool}" being called`,
                                    iterationsUsed: currentIteration,
                                };
                            }
                        }

                        // Check if any tool requested stopping the conversation
                        if (this.shouldStopConversation) {
                            span.setStatus({ code: 1 });
                            span.setAttributes({
                                'tools.iterations_used': currentIteration,
                                'tools.stopped_by_tool': true,
                                'tools.cumulative_prompt_tokens': cumulativePromptTokens,
                                'tools.cumulative_completion_tokens': cumulativeCompletionTokens,
                                'tools.cumulative_total_tokens': cumulativeTotalTokens,
                            });
                            return {
                                success: true,
                                result: 'Stopped by tool request',
                                iterationsUsed: currentIteration,
                            };
                        }

                        // Get the next LLM response which may include more tool calls
                        const messages = conversation.getMessagesForAPI();
                        const toolDefinitions = this.getToolDefinitions();
                        const response = await onIteration(messages, toolDefinitions);

                        conversation.addUsage(response.usage);

                        // Accumulate token usage for tracing
                        cumulativePromptTokens += response.usage.input_tokens;
                        cumulativeCompletionTokens += response.usage.output_tokens;
                        cumulativeTotalTokens += response.usage.total_tokens;

                        // Use the response content directly since images are now injected as user input
                        const messageContent: string | (TextContent | ImageContent)[] =
                            response.content;

                        // Check if the last message is already an assistant message
                        // (this happens when streaming is enabled and the message was already added/updated)
                        const conversationMessages = conversation.getMessages();
                        const lastMessage =
                            conversationMessages.length > 0
                                ? conversationMessages[conversationMessages.length - 1]
                                : null;
                        const messageAlreadyAdded = lastMessage && isAssistantMessage(lastMessage);

                        if (response.tool_calls && response.tool_calls.length > 0) {
                            // LLM wants to call more tools - continue the iteration
                            if (!messageAlreadyAdded) {
                                conversation.addAssistantMessage(
                                    messageContent,
                                    response.tool_calls,
                                    response.usage,
                                    response.code_interpreter_outputs,
                                    response.openai_response_id,
                                );
                            }
                            currentToolCalls = response.tool_calls;
                        } else {
                            // LLM provided a final response without tool calls - we're done
                            if (!messageAlreadyAdded) {
                                conversation.addAssistantMessage(
                                    messageContent,
                                    undefined,
                                    response.usage,
                                    response.code_interpreter_outputs,
                                    response.openai_response_id,
                                );
                            }
                            span.setStatus({ code: 1 });
                            span.setAttributes({
                                'tools.iterations_used': currentIteration,
                                'tools.completed_naturally': true,
                                'tools.cumulative_prompt_tokens': cumulativePromptTokens,
                                'tools.cumulative_completion_tokens': cumulativeCompletionTokens,
                                'tools.cumulative_total_tokens': cumulativeTotalTokens,
                            });
                            return {
                                success: true,
                                result: messageContent,
                                iterationsUsed: currentIteration,
                            };
                        }
                    } else {
                        // No tool calls to execute - we're done
                        span.setStatus({ code: 1 });
                        span.setAttributes({
                            'tools.iterations_used': currentIteration,
                            'tools.no_initial_tools': true,
                            'tools.cumulative_prompt_tokens': cumulativePromptTokens,
                            'tools.cumulative_completion_tokens': cumulativeCompletionTokens,
                            'tools.cumulative_total_tokens': cumulativeTotalTokens,
                        });
                        return {
                            success: true,
                            result: 'No more tool calls to execute',
                            iterationsUsed: currentIteration,
                        };
                    }
                }

                // Cap hit — close out the turn so the chat stays usable.
                span.setStatus({ code: 2, message: 'Maximum tool execution iterations reached' });
                span.setAttributes({
                    'tools.iterations_used': currentIteration,
                    'tools.max_iterations_reached': true,
                    'tools.cumulative_prompt_tokens': cumulativePromptTokens,
                    'tools.cumulative_completion_tokens': cumulativeCompletionTokens,
                    'tools.cumulative_total_tokens': cumulativeTotalTokens,
                });
                finalizeUnrunToolCalls(conversation, currentToolCalls ?? null);
                return {
                    success: true,
                    result: MAX_ITERATIONS_NOTICE,
                    iterationsUsed: currentIteration,
                };
            } finally {
                span.end();
            }
        });
    }

    getMaxIterations(): number {
        return this.maxIterations;
    }

    setMaxIterations(maxIterations: number): void {
        this.maxIterations = maxIterations;
    }

    getToolDefinitions(): ToolDefinition[] {
        return this.tools
            .filter((tool) => tool.isLLMCallable)
            .map((tool) => tool.getToolDefinition());
    }
}
